import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

interface CacheEntry {
  data: unknown;
  expiry: number;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private apiUrl = environment.apiUrl;
  private isBrowser: boolean;
  /** In-memory cache for GET list endpoints (key = endpoint + JSON params, TTL per entry) */
  private cache = new Map<string, CacheEntry>();

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  private getHeaders(): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    if (this.isBrowser) {
      const token = localStorage.getItem('token');
      if (token) {
        headers = headers.set('Authorization', `Bearer ${token}`);
      }
    }

    return headers;
  }

  get<T>(endpoint: string, params?: any): Observable<T> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        httpParams = httpParams.set(key, params[key]);
      });
    }

    return this.http.get<T>(`${this.apiUrl}/${endpoint}`, {
      headers: this.getHeaders(),
      params: httpParams
    });
  }

  /**
   * GET with short-lived cache. Use for list endpoints (e.g. categories) to avoid repeated requests when switching tabs.
   * @param ttlMs Cache TTL in ms (default 60s). Set to 0 to bypass cache.
   */
  getWithCache<T>(endpoint: string, params?: any, ttlMs: number = 60000): Observable<T> {
    const key = endpoint + JSON.stringify(params ?? {});
    const entry = this.cache.get(key);
    if (ttlMs > 0 && entry && Date.now() < entry.expiry) {
      return of(entry.data as T);
    }
    return this.get<T>(endpoint, params).pipe(
      tap(data => {
        if (ttlMs > 0) {
          this.cache.set(key, { data, expiry: Date.now() + ttlMs });
        }
      })
    );
  }

  /** Clear GET cache (e.g. after create/update/delete so next load is fresh). */
  clearCache(): void {
    this.cache.clear();
  }

  post<T>(endpoint: string, data: any): Observable<T> {
    return this.http.post<T>(`${this.apiUrl}/${endpoint}`, data, {
      headers: this.getHeaders()
    });
  }

  put<T>(endpoint: string, data: any): Observable<T> {
    return this.http.put<T>(`${this.apiUrl}/${endpoint}`, data, {
      headers: this.getHeaders()
    });
  }

  delete<T>(endpoint: string, data?: any): Observable<T> {
    const options = {
      headers: this.getHeaders(),
      body: data
    };
    return this.http.delete<T>(`${this.apiUrl}/${endpoint}`, options);
  }
}




